/* Real HTTP API implementation of OrdersClient */

import axios, { AxiosInstance, AxiosResponse, CancelToken } from 'axios';
import { getApiConfig } from '../../environment';
import { IOrdersClient } from './IOrdersClient';
import {
  Result,
  PaginatedList,
  Order,
  OrderDetails,
  OrderType,
  DeliverySchedule,
  Supplier,
  Category,
  OrderForm,
  ShippingDetailsRequest,
  ContainerFillResult,
  FileResponse,
  FileRequest,
  CreateOrderRequest,
  AddOrderItemRequest,
  DeleteOrderItemsRequest,
  OrderUpdate,
  OrderFormUpdate,
  OrdersFilterOptions,
  OrderlySystem,
  Item,
  ItemDocument,
  ItemsFilterOptions,
  ItemFilterParams,
  Catalogue,
  CatalogueFilterOptions,
  CatalogueProducts,
  CatalogueProductsFilterOptions,
  ProductAttributeModel,
  ProductAttributesRequest,
  CurrencyConversionContent,
  ExchangeRateRequest,
  News,
  OutOfStockUrgentAction,
  DefaultOrderSettings,
  DeliveryMethodListModel,
  Instance,
  InstanceFilterOptions,
  Market,
  MarketFilterOptions,
  MarketOrderType,
  MarketOrderTypeFilterOptions,
  MarketSupplier,
  MarketSupplierFilterOptions,
  MarketCatalogue,
  MarketCatalogueFilterOptions,
  MarketStore,
  MarketStoreFilterOptions,
  MarketStoreUploadRequest,
  MarketStoreUploadResult,
  UomType,
  UomTypeFilterOptions,
  MarketCategory,
  MarketCategoryFilterOptions,
  MarketItem,
  MarketItemFilterOptions,
  MarketItemUploadRequest,
  MarketItemUploadResult,
  AutoshipTemplate,
  AutoshipTemplateModel,
  AutoshipmentTemplatesFilterOptions,
  AutoshipSchedule,
  AutoshipScheduleModel,
  AutoshipmentSchedulesFilterOptions,
  StoreAvailableODS,
  IdNameModel,
  RetailerEdnOrderModel,
  BulkODSDateUpdateModel,
  DeliveryScheduleFilterOptions,
  DeliveryScheduleModel,
  AddODSModel,
  ODSImportResultModel,
  ForecastOrdersReportRequest,
} from './ordersTypes';

export class OrdersClientApi implements IOrdersClient {
  private axiosInstance: AxiosInstance;

  constructor() {
    const apiConfig = getApiConfig();
    this.axiosInstance = axios.create({
      baseURL: apiConfig.apiBaseUrl,
    });

    this.axiosInstance.interceptors.request.use(async (config) => {
      const token = await (window as { Clerk?: { session?: { getToken?: (options: { template: string }) => Promise<string> } } }).Clerk?.session?.getToken?.({ template: 'Orderly_default' });
      if (!token) {
        throw new Error('Authorization token is missing.');
      }
      const currentUser = localStorage.getItem('user');
      const user = currentUser ? JSON.parse(currentUser) : null;

      config.headers['Accept'] = 'application/json';
      config.headers['Authorization'] = `Bearer ${token}`;
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers['Expires'] = '-1';
      config.headers['If-Modified-Since'] = 'Thu, 13 Feb 1985 13:40:01 GMT';
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['Pragma'] = 'no-cache';
      config.headers['user-email'] = user?.emailAddress || '';

      return config;
    }, (error) => {
      return Promise.reject(error);
    });
  }

  // Orders CRUD
  async getOrders(options: OrdersFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Order>>> {
    const params = new URLSearchParams({
      StoreNumber: options.storeNumber ?? '',
      OrderNumber: options.orderNumber ?? '',
      Item: options.item ?? '',
      OrderType: options.orderType.toString() ?? '',
      Status: options.status.toString() ?? '',
      DeliveryDateFrom: options.deliveryDateFrom ?? '',
      DeliveryDateTo: options.deliveryDateTo ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': options.storeNumber };
    const response: AxiosResponse<Result<PaginatedList<Order>>> = await this.axiosInstance.get(`orders?${params}`, { headers, cancelToken });
    return response.data;
  }

  async getOrder(storeNumber: string | undefined, orderId: number, cancelToken?: CancelToken): Promise<Result<OrderDetails>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<OrderDetails>> = await this.axiosInstance.get(`orders/${orderId}`, { headers, cancelToken });
    return response.data;
  }

  async getOrderByDeliveryDate(storeNumber: string | undefined, orderTypeId: number, deliveryDate: string, supplierNumber: string, cancelToken?: CancelToken): Promise<Result<OrderDetails>> {
    const params = new URLSearchParams({
      SupplierNumber: supplierNumber,
    }).toString();
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<OrderDetails>> = await this.axiosInstance.get(`orders/ordertype/${orderTypeId.toString()}/deliverydate/${deliveryDate}?${params}`, { headers, cancelToken });
    return response.data;
  }

  async createOrder(request: CreateOrderRequest, cancelToken?: CancelToken): Promise<Result<number>> {
    const headers = { 'ews-storeNumber': request.storeNumber };
    if (request.orderlySystem === undefined) {
      request.orderlySystem = OrderlySystem.OFS;
    }
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post<Result<number>>('orders', request, { headers, cancelToken });
    return response.data;
  }

  async updateOrder(storeNumber: string | undefined, request: OrderUpdate, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    if (request.orderlySystem === undefined) {
      request.orderlySystem = OrderlySystem.OFS;
    }
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('orders/update', request, { headers, cancelToken });
    return response.data;
  }

  async deleteOrder(storeNumber: string | undefined, orderId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(`orders/${orderId}`, { headers, cancelToken });
    return response.data;
  }

  async duplicateOrder(storeNumber: string | undefined, sourceOrderId: number, cancelToken?: CancelToken): Promise<Result<number>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post(`orders/duplicate/${sourceOrderId}`, null, { headers, cancelToken });
    return response.data;
  }

  async copyOrder(storeNumber: string | undefined, orderId: string | undefined, copyOrderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/${orderId}/copy/${copyOrderId}`, null, { headers, cancelToken });
    return response.data;
  }

  // Order Items
  async addOrderItem(storeNumber: string | undefined, request: AddOrderItemRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post<Result<boolean>>('orders/orderitems/add', request, { headers, cancelToken });
    return response.data;
  }

  async deleteOrderItem(storeNumber: string | undefined, orderId: string, orderItemId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const url = `orders/${orderId}/OrderItems/${orderItemId}`;
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(url, { headers, cancelToken });
    return response.data;
  }

  async deleteOrderItems(storeNumber: string | undefined, request: DeleteOrderItemsRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post<Result<boolean>>('orders/deleteorderitems', request, { headers, cancelToken });
    return response.data;
  }

  async deleteAllOrderItems(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post<Result<boolean>>(`orders/deleteallorderitems/${orderId}`, {}, { headers, cancelToken });
    return response.data;
  }

  // Order Submission
  async checkoutOrder(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post<Result<boolean>>(`orders/checkoutorder/${orderId}`, {}, { headers, cancelToken });
    return response.data;
  }

  async submitOrder(storeNumber: string | undefined, orderId: string, orderDetails: OrderDetails, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/submit/${orderId}`, orderDetails, { headers, cancelToken });
    return response.data;
  }

  // Order Types & Schedules
  async getOrderTypes(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<OrderType[]>> {
    const params = new URLSearchParams({
      StoreNumber: storeNumber ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<OrderType[]>> = await this.axiosInstance.get(`ordertypes?${params}`, { headers, cancelToken });
    return response.data;
  }

  async getDeliverySchedules(storeNumber: string | undefined, orderType: number | undefined, supplierNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<DeliverySchedule[]>> {
    const params = new URLSearchParams({
      StoreNumber: storeNumber ?? '',
      OrderType: orderType?.toString() ?? '',
      SupplierNumber: supplierNumber ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<DeliverySchedule[]>> = await this.axiosInstance.get(`deliveryschedules?${params}`, { headers, cancelToken });
    return response.data;
  }

  async getSuppliers(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<Supplier[]>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<Supplier[]>> = await this.axiosInstance.get('suppliers', { headers, cancelToken });
    return response.data;
  }

  async getCategories(storeNumber: string | undefined, orderType: number, supplier: string | undefined, cancelToken?: CancelToken): Promise<Result<Category[]>> {
    const params = new URLSearchParams({
      StoreNumber: storeNumber ?? '',
      OrderType: orderType.toString(),
      Supplier: supplier?.toString() ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<Category[]>> = await this.axiosInstance.get(`categories?${params}`, { headers, cancelToken });
    return response.data;
  }

  // Order Form
  async getOrderForm(storeNumber: string | undefined, orderTypeId: number, categoryId: number, sort: string, direction: string, item: string, supplierNumber: string, cancelToken?: CancelToken): Promise<Result<OrderForm>> {
    const params = new URLSearchParams({
      OrderType: orderTypeId.toString() ?? '',
      Category: categoryId?.toString() ?? '',
      Sort: sort ?? '',
      Direction: direction ?? '',
      Item: item ?? '',
      SupplierNumber: supplierNumber ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<OrderForm>> = await this.axiosInstance.get(`orders/orderform?${params}`, { headers, cancelToken });
    return response.data;
  }

  async saveOrderForm(storeNumber: string | undefined, request: OrderFormUpdate, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('orders/orderform', request, { headers, cancelToken });
    return response.data;
  }

  async downloadOrderForm(storeNumber: string | undefined, orderTypeId: number, supplierNumber: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    const params = new URLSearchParams({ OrderType: orderTypeId.toString(), Supplier: supplierNumber }).toString();
    const headers = { 'ews-storeNumber': storeNumber ?? '' };
    const response = await this.axiosInstance.get<Result<FileResponse>>(`orders/orderform/download?${params}`, { headers, cancelToken });
    return response.data;
  }

  async uploadOrderForm(storeNumber: string | undefined, orderTypeId: number, supplierNumber: string, request: FileRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const params = new URLSearchParams({ Supplier: supplierNumber }).toString();
    const headers = { 'ews-storeNumber': storeNumber ?? '' };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/orderform/upload/${orderTypeId}?${params}`, request, { headers, cancelToken });
    return response.data;
  }

  // Order Spreadsheet
  async downloadOrderSpreadsheet(storeNumber: string | undefined, orderId: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    const headers = { 'ews-storeNumber': storeNumber ?? '' };
    const response = await this.axiosInstance.get<Result<FileResponse>>(`orders/${orderId}/download`, { headers, cancelToken });
    return response.data;
  }

  async uploadOrderSpreadsheet(storeNumber: string | undefined, orderId: string, request: FileRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber ?? '' };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/${orderId}/upload`, request, { headers, cancelToken });
    return response.data;
  }

  // Shipping
  async getShippingDetails(storeNumber: string, orderId: string, shippingMethodId?: string, cancelToken?: CancelToken): Promise<Result<ShippingDetailsRequest>> {
    const params = new URLSearchParams({
      storeNumber: storeNumber ?? '',
      orderId: orderId ?? '',
      shippingMethodId: shippingMethodId ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<ShippingDetailsRequest>> = await this.axiosInstance.get(`orders/getShippingDetails?${params}`, { headers, cancelToken });
    return response.data;
  }

  async setShippingDetails(storeNumber: string, orderId: string, shippingDetails: ShippingDetailsRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const params = new URLSearchParams({
      storeNumber: storeNumber ?? '',
      orderId: orderId ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/setShippingDetails?${params}`, shippingDetails, { headers, cancelToken });
    return response.data;
  }

  async getStoreContainerData(storeNumber: string | undefined, orderNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<ContainerFillResult>> {
    const params = new URLSearchParams({
      storeNumber: storeNumber ?? '',
      orderId: orderNumber ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<ContainerFillResult>> = await this.axiosInstance.get(`orders/storeContainer?${params}`, { headers, cancelToken });
    return response.data;
  }

  // Audit
  async getOrderAudit(storeNumber: string | undefined, orderNumber: number, cancelToken?: CancelToken): Promise<Result<(string | undefined | null)[]>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<(string | undefined | null)[]>> = await this.axiosInstance.get(`orders/audit/${orderNumber}`, { headers, cancelToken });
    return response.data;
  }

  // Order Approval
  async approveOrder(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/approve/${orderId}`, null, { headers, cancelToken });
    return response.data;
  }

  async approveAmendment(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/approve-amendment/${orderId}`, null, { headers, cancelToken });
    return response.data;
  }

  async rejectOrder(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/reject/${orderId}`, null, { headers, cancelToken });
    return response.data;
  }

  async rejectAmendment(storeNumber: string | undefined, orderId: string | undefined, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`orders/reject-amendment/${orderId}`, null, { headers, cancelToken });
    return response.data;
  }

  // Order Delivery Date
  async updateOrderDeliveryDate(storeNumber: string | undefined, orderId: string, deliveryDate: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(
      `orders/${orderId}/deliverydate`,
      { deliveryDate },
      { headers, cancelToken },
    );
    return response.data;
  }

  // Items
  async getItems(options: ItemsFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Item>>> {
    const params = new URLSearchParams({
      StoreNumber: options.storeNumber ?? '',
      Item: options.item ?? '',
      OrderType: options.orderType.toString() ?? '',
      Category: options.categoryId?.toString() ?? '',
      Supplier: options.supplier?.toString() ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
      ItemIds: options?.itemIds ? options.itemIds.join(',') : '',
    }).toString();
    const headers = { 'ews-storeNumber': options.storeNumber };
    const response: AxiosResponse<Result<PaginatedList<Item>>> = await this.axiosInstance.get(`products?${params}`, { headers, cancelToken });
    return response.data;
  }

  async getItemsByFranchiseName(options: ItemFilterParams, cancelToken?: CancelToken): Promise<Result<PaginatedList<Item>>> {
    const params = new URLSearchParams({
      MarketName: options.marketName ?? '',
      StoreNumber: options.storeNumber ?? '',
      Search: options.search ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
    }).toString();
    const headers = { 'ews-storeNumber': options.storeNumber };
    const response: AxiosResponse<Result<PaginatedList<Item>>> = await this.axiosInstance.get(`products/getbymarketname?${params}`, { headers, cancelToken });
    return response.data;
  }

  async getItem(storeNumber: string | undefined, itemId: number, cancelToken?: CancelToken): Promise<Result<Item>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<Item>> = await this.axiosInstance.get(`products/${itemId}`, { headers, cancelToken });
    return response.data;
  }

  async getItemByBarcode(storeNumber: string | undefined, barcode: string, stockCount: string | undefined, user: string | undefined, cancelToken?: CancelToken): Promise<Result<Item>> {
    const headers = { 'ews-storeNumber': storeNumber, 'stock-count': stockCount, 'user': user };
    const response: AxiosResponse<Result<Item>> = await this.axiosInstance.get(`products/barcode/${barcode}`, { headers, cancelToken });
    return response.data;
  }

  async getItemBySku(storeNumber: string | undefined, sku: string, forOfd?: boolean, cancelToken?: CancelToken): Promise<Result<Item>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<Item>> = await this.axiosInstance.get(`products/sku/${sku}?ForOfd=${forOfd ?? false}`, { headers, cancelToken });
    return response.data;
  }

  async getItemDocuments(storeNumber: string | undefined, sku: string, cancelToken?: CancelToken): Promise<Result<ItemDocument[]>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<ItemDocument[]>> = await this.axiosInstance.get(`products/documents/${sku}`, { headers, cancelToken });
    return response.data;
  }

  async getOrderDocuments(storeNumber: string | undefined, orderId: number, cancelToken?: CancelToken): Promise<Result<{ [itemId: number]: ItemDocument[] }>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<{ Key: number; Value: ItemDocument[] }[]>> = await this.axiosInstance.get(`orders/${orderId}/documents`, { headers, cancelToken });
    if (response.data.success && response.data.data) {
      const transformedData: { [itemId: number]: ItemDocument[] } = {};
      response.data.data.forEach(item => {
        transformedData[item.Key] = item.Value;
      });
      return {
        success: response.data.success,
        message: response.data.message,
        data: transformedData,
      };
    }
    return {
      success: response.data.success || false,
      message: response.data.message || '',
      data: {},
    };
  }

  async getItemAttributes(storeNumber: string | undefined, sku: string, cancelToken?: CancelToken): Promise<Result<ProductAttributeModel[]>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<ProductAttributeModel[]>> = await this.axiosInstance.get(`itemattributes/${sku}`, { headers, cancelToken });
    return response.data;
  }

  async updateItemAttributes(storeNumber: string | undefined, request: ProductAttributesRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('items/attributes', request, { headers, cancelToken });
    return response.data;
  }

  // Catalogues
  async getCatalogues(options: CatalogueFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Catalogue>>> {
    const params = new URLSearchParams({
      Name: options.name ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': options.storeNumber };
    const response: AxiosResponse<Result<PaginatedList<Catalogue>>> = await this.axiosInstance.get(`catalogues?${params}`, { headers, cancelToken });
    return response.data;
  }

  async getCatalogueRetailers(catalogueId: string, storeNumber: string, cancelToken?: CancelToken): Promise<Result<string[]>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<string[]>> = await this.axiosInstance.get(`catalogues/${catalogueId}/retailers`, { headers, cancelToken });
    return response.data;
  }

  async getCatalogueProducts(catalogueId: string, options: CatalogueProductsFilterOptions, cancelToken?: CancelToken): Promise<Result<CatalogueProducts>> {
    const params = new URLSearchParams({
      SearchTerm: options.searchTerm ?? '',
      Category: options.category ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': options.storeNumber };
    const response: AxiosResponse<Result<CatalogueProducts>> = await this.axiosInstance.get(`catalogueProducts/${catalogueId}?${params}`, { headers, cancelToken });
    return response.data;
  }

  async getCategoriesByCatalogue(storeNumber: string, catalogueId: string, cancelToken?: CancelToken): Promise<Result<Category[]>> {
    const params = new URLSearchParams({ CatalogueId: catalogueId }).toString();
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<Category[]>> = await this.axiosInstance.get(`categoriesbycatalogue?${params}`, { headers, cancelToken });
    return response.data;
  }

  async downloadCatalogueExportAttributes(storeNumber: string | undefined, catalogueId: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    const headers = { 'ews-storeNumber': storeNumber ?? '' };
    const response = await this.axiosInstance.get<Result<FileResponse>>(`catalogues/${catalogueId}/exportattributes/download`, { headers, cancelToken });
    return response.data;
  }

  async uploadCatalogueExportAttributes(storeNumber: string | undefined, catalogueId: string, request: FileRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber ?? '' };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`catalogues/${catalogueId}/exportattributes/upload`, request, { headers, cancelToken });
    return response.data;
  }

  // Currency
  async getExchangeRates(storeNumber: string | undefined, pageNumber: number, pageSize: number, active: boolean, cancelToken?: CancelToken): Promise<Result<PaginatedList<CurrencyConversionContent>>> {
    const params = new URLSearchParams({
      PageNumber: pageNumber?.toString() ?? '',
      PageSize: pageSize?.toString() ?? '',
      Active: active ? 'true' : 'false',
    }).toString();
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<PaginatedList<CurrencyConversionContent>>> = await this.axiosInstance.get(`currency?${params}`, { headers, cancelToken });
    return response.data;
  }

  async updateExchangeRate(storeNumber: string | undefined, request: ExchangeRateRequest, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('currency', request, { headers, cancelToken });
    return response.data;
  }

  async deleteExchangeRate(storeNumber: string | undefined, id: number, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(`currency?id=${id}`, { headers, cancelToken });
    return response.data;
  }

  // News & Alerts
  async getNews(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<News[]>> {
    const params = new URLSearchParams({
      StoreNumber: storeNumber ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<News[]>> = await this.axiosInstance.get(`news?${params}`, { headers, cancelToken });
    return response.data;
  }

  async getOutOfStockAlert(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<OutOfStockUrgentAction>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<OutOfStockUrgentAction>> = await this.axiosInstance.get('outOfStockAlert', { headers, cancelToken });
    return response.data;
  }

  async getUrgentAlerts(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<OutOfStockUrgentAction>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<OutOfStockUrgentAction>> = await this.axiosInstance.get('urgentAlert', { headers, cancelToken });
    return response.data;
  }

  // Settings
  async getDefaultOrderSettings(storeNumber: string | undefined, cancelToken?: CancelToken): Promise<Result<DefaultOrderSettings>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<DefaultOrderSettings>> = await this.axiosInstance.get('customers/getdefaultordersettings', { headers, cancelToken });
    return response.data;
  }

  async updateDefaultOrderSettings(storeNumber: string | undefined, request: DefaultOrderSettings, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('customers/updatedefaultordersettings', request, { headers, cancelToken });
    return response.data;
  }

  async getOnboardingDeliveryMethods(storeNumber?: string, cancelToken?: CancelToken): Promise<Result<DeliveryMethodListModel>> {
    const params = new URLSearchParams({
      StoreNumber: storeNumber ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<DeliveryMethodListModel>> = await this.axiosInstance.get(`onboarding/deliverymethods?${params}`, { headers, cancelToken });
    return response.data;
  }

  // Instances
  async getInstance(instanceId: string, cancelToken?: CancelToken): Promise<Result<Instance>> {
    const response: AxiosResponse<Result<Instance>> = await this.axiosInstance.get(`instances/${instanceId}`, { cancelToken });
    return response.data;
  }

  async getInstances(options: InstanceFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Instance>>> {
    const params = new URLSearchParams({
      Name: options.name ?? '',
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
    }).toString();
    const response: AxiosResponse<Result<PaginatedList<Instance>>> = await this.axiosInstance.get(`instances?${params}`, { cancelToken });
    return response.data;
  }

  async createInstance(request: Instance, cancelToken?: CancelToken): Promise<Result<number>> {
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('instances', request, { cancelToken });
    return response.data;
  }

  async updateInstance(instanceId: string, request: Instance, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`instances/${instanceId}`, request, { cancelToken });
    return response.data;
  }

  // Markets
  async getMarket(marketId: string, cancelToken?: CancelToken): Promise<Result<Market>> {
    const response: AxiosResponse<Result<Market>> = await this.axiosInstance.get(`markets/${marketId}`, { cancelToken });
    return response.data;
  }

  async getMarkets(options: MarketFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<Market>>> {
    const params = new URLSearchParams({
      CompanyId: options.companyId ?? '',
      Name: options.name ?? '',
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
    }).toString();
    const response: AxiosResponse<Result<PaginatedList<Market>>> = await this.axiosInstance.get(`markets?${params}`, { cancelToken });
    return response.data;
  }

  async createMarket(request: Market, cancelToken?: CancelToken): Promise<Result<number>> {
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('markets', request, { cancelToken });
    return response.data;
  }

  async updateMarket(marketId: string, request: Market, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`markets/${marketId}`, request, { cancelToken });
    return response.data;
  }

  // Market Order Types
  async getMarketOrderType(marketOrderTypeId: string, cancelToken?: CancelToken): Promise<Result<MarketOrderType>> {
    const response: AxiosResponse<Result<MarketOrderType>> = await this.axiosInstance.get(`marketordertypes/${marketOrderTypeId}`, { cancelToken });
    return response.data;
  }

  async getMarketOrderTypes(options: MarketOrderTypeFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketOrderType>>> {
    const params = new URLSearchParams({
      CompanyId: options.companyId ?? '',
      CustomerTypeId: options.customerTypeId ?? '',
      SearchTerm: options.searchTerm ?? '',
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
    }).toString();
    const response: AxiosResponse<Result<PaginatedList<MarketOrderType>>> = await this.axiosInstance.get(`marketordertypes?${params}`, { cancelToken });
    return response.data;
  }

  async createMarketOrderType(request: MarketOrderType, cancelToken?: CancelToken): Promise<Result<number>> {
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('marketordertypes', request, { cancelToken });
    return response.data;
  }

  async updateMarketOrderType(marketOrderTypeId: string, request: MarketOrderType, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`marketordertypes/${marketOrderTypeId}`, request, { cancelToken });
    return response.data;
  }

  // Market Suppliers
  async getMarketSupplier(supplierId: string, cancelToken?: CancelToken): Promise<Result<MarketSupplier>> {
    const response: AxiosResponse<Result<MarketSupplier>> = await this.axiosInstance.get(`marketsuppliers/${supplierId}`, { cancelToken });
    return response.data;
  }

  async getMarketSuppliers(options: MarketSupplierFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketSupplier>>> {
    const params = new URLSearchParams({
      CompanyId: options.companyId ?? '',
      CustomerTypeId: options.customerTypeId ?? '',
      SearchTerm: options.searchTerm ?? '',
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
    }).toString();
    const response: AxiosResponse<Result<PaginatedList<MarketSupplier>>> = await this.axiosInstance.get(`marketsuppliers?${params}`, { cancelToken });
    return response.data;
  }

  async createMarketSupplier(request: MarketSupplier, cancelToken?: CancelToken): Promise<Result<number>> {
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('marketsuppliers', request, { cancelToken });
    return response.data;
  }

  async updateMarketSupplier(supplierId: string, request: MarketSupplier, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`marketsuppliers/${supplierId}`, request, { cancelToken });
    return response.data;
  }

  // Market Catalogues
  async getMarketCatalogue(catalogueId: string, cancelToken?: CancelToken): Promise<Result<MarketCatalogue>> {
    const response: AxiosResponse<Result<MarketCatalogue>> = await this.axiosInstance.get(`marketcatalogues/${catalogueId}`, { cancelToken });
    return response.data;
  }

  async getMarketCatalogues(options: MarketCatalogueFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketCatalogue>>> {
    const params = new URLSearchParams({
      CompanyId: options.companyId ?? '',
      CustomerTypeId: options.customerTypeId ?? '',
      SearchTerm: options.searchTerm ?? '',
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
    }).toString();
    const response: AxiosResponse<Result<PaginatedList<MarketCatalogue>>> = await this.axiosInstance.get(`marketcatalogues?${params}`, { cancelToken });
    return response.data;
  }

  async createMarketCatalogue(request: MarketCatalogue, cancelToken?: CancelToken): Promise<Result<number>> {
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('marketcatalogues', request, { cancelToken });
    return response.data;
  }

  async updateMarketCatalogue(catalogueId: string, request: MarketCatalogue, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`marketcatalogues/${catalogueId}`, request, { cancelToken });
    return response.data;
  }

  // Market Stores
  async getMarketStore(storeId: string, cancelToken?: CancelToken): Promise<Result<MarketStore>> {
    const response: AxiosResponse<Result<MarketStore>> = await this.axiosInstance.get(`marketstores/${storeId}`, { cancelToken });
    return response.data;
  }

  async getMarketStores(options: MarketStoreFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketStore>>> {
    const params = new URLSearchParams({
      CompanyId: options.companyId ?? '',
      CustomerTypeId: options.customerTypeId ?? '',
      SearchTerm: options.searchTerm ?? '',
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
    }).toString();
    const response: AxiosResponse<Result<PaginatedList<MarketStore>>> = await this.axiosInstance.get(`marketstores?${params}`, { cancelToken });
    return response.data;
  }

  async createMarketStore(request: MarketStore, cancelToken?: CancelToken): Promise<Result<number>> {
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('marketstores', request, { cancelToken });
    return response.data;
  }

  async updateMarketStore(storeId: string, request: MarketStore, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`marketstores/${storeId}`, request, { cancelToken });
    return response.data;
  }

  async downloadMarketStores(companyId: string, customerTypeId: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    const params = new URLSearchParams({
      CompanyId: companyId,
      CustomerTypeId: customerTypeId,
    }).toString();
    const response: AxiosResponse<Result<FileResponse>> = await this.axiosInstance.get(`marketstores/download?${params}`, { cancelToken });
    return response.data;
  }

  async uploadMarketStores(request: MarketStoreUploadRequest, cancelToken?: CancelToken): Promise<Result<MarketStoreUploadResult>> {
    const response: AxiosResponse<Result<MarketStoreUploadResult>> = await this.axiosInstance.post('marketstores/upload', request, { cancelToken });
    return response.data;
  }

  // UOM Types
  async getUomType(uomTypeId: string, cancelToken?: CancelToken): Promise<Result<UomType>> {
    const response: AxiosResponse<Result<UomType>> = await this.axiosInstance.get(`uomtypes/${uomTypeId}`, { cancelToken });
    return response.data;
  }

  async getUomTypes(options: UomTypeFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<UomType>>> {
    const params = new URLSearchParams({
      CompanyId: options.companyId ?? '',
      SearchTerm: options.searchTerm ?? '',
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
    }).toString();
    const response: AxiosResponse<Result<PaginatedList<UomType>>> = await this.axiosInstance.get(`uomtypes?${params}`, { cancelToken });
    return response.data;
  }

  async createUomType(request: UomType, cancelToken?: CancelToken): Promise<Result<number>> {
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('uomtypes', request, { cancelToken });
    return response.data;
  }

  async updateUomType(uomTypeId: string, request: UomType, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`uomtypes/${uomTypeId}`, request, { cancelToken });
    return response.data;
  }

  // Market Categories
  async getMarketCategory(categoryId: string, cancelToken?: CancelToken): Promise<Result<MarketCategory>> {
    const response: AxiosResponse<Result<MarketCategory>> = await this.axiosInstance.get(`marketcategories/${categoryId}`, { cancelToken });
    return response.data;
  }

  async getMarketCategories(options: MarketCategoryFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketCategory>>> {
    const params = new URLSearchParams({
      CompanyId: options.companyId ?? '',
      SearchTerm: options.searchTerm ?? '',
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
    }).toString();
    const response: AxiosResponse<Result<PaginatedList<MarketCategory>>> = await this.axiosInstance.get(`marketcategories?${params}`, { cancelToken });
    return response.data;
  }

  async createMarketCategory(request: MarketCategory, cancelToken?: CancelToken): Promise<Result<number>> {
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('marketcategories', request, { cancelToken });
    return response.data;
  }

  async updateMarketCategory(categoryId: string, request: MarketCategory, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`marketcategories/${categoryId}`, request, { cancelToken });
    return response.data;
  }

  // Market Items
  async getMarketItem(itemId: string, companyId: string, customerTypeId: string, cancelToken?: CancelToken): Promise<Result<MarketItem>> {
    const response: AxiosResponse<Result<MarketItem>> = await this.axiosInstance.get(`marketitems/${itemId}/instance/${companyId}/market/${customerTypeId}`, { cancelToken });
    return response.data;
  }

  async getMarketItems(options: MarketItemFilterOptions, cancelToken?: CancelToken): Promise<Result<PaginatedList<MarketItem>>> {
    const params = new URLSearchParams({
      CompanyId: options.companyId ?? '',
      CustomerTypeId: options.customerTypeId ?? '',
      SearchTerm: options.searchTerm ?? '',
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
    }).toString();
    const response: AxiosResponse<Result<PaginatedList<MarketItem>>> = await this.axiosInstance.get(`marketitems?${params}`, { cancelToken });
    return response.data;
  }

  async createMarketItem(request: MarketItem, cancelToken?: CancelToken): Promise<Result<number>> {
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('marketitems', request, { cancelToken });
    return response.data;
  }

  async updateMarketItem(itemId: string, request: MarketItem, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`marketitems/${itemId}`, request, { cancelToken });
    return response.data;
  }

  async downloadMarketItems(companyId: string, customerTypeId: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    const params = new URLSearchParams({
      CompanyId: companyId,
      CustomerTypeId: customerTypeId,
    }).toString();
    const response: AxiosResponse<Result<FileResponse>> = await this.axiosInstance.get(`marketitems/download?${params}`, { cancelToken });
    return response.data;
  }

  async uploadMarketItems(request: MarketItemUploadRequest, cancelToken?: CancelToken): Promise<Result<MarketItemUploadResult>> {
    const response: AxiosResponse<Result<MarketItemUploadResult>> = await this.axiosInstance.post('marketitems/upload', request, { cancelToken });
    return response.data;
  }

  // Autoship Templates
  async createOrUpdateAutoshipTemplate(request: AutoshipTemplate, storeNumber: string, cancelToken?: CancelToken): Promise<Result<number>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('autoshiptemplate', request, { headers, cancelToken });
    return response.data;
  }

  async getAutoshipTemplates(options: AutoshipmentTemplatesFilterOptions, storeNumber: string, cancelToken?: CancelToken): Promise<Result<PaginatedList<AutoshipTemplateModel>>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const params = new URLSearchParams({
      Name: options.name ?? '',
      OrderTypeId: options.orderTypeId.toString(),
      StatusId: options.statusId.toString(),
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
    }).toString();
    const response: AxiosResponse<Result<PaginatedList<AutoshipTemplateModel>>> = await this.axiosInstance.get(`autoshiptemplate?${params}`, { headers, cancelToken });
    return response.data;
  }

  async getAutoshipTemplateById(id: string, storeNumber: string, includeExpandedDetails: boolean = false, cancelToken?: CancelToken): Promise<Result<AutoshipTemplateModel>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<AutoshipTemplateModel>> = await this.axiosInstance.get(`autoshiptemplate/${id}?includeExpandedDetails=${includeExpandedDetails}`, { headers, cancelToken });
    return response.data;
  }

  async getAutoshipTemplateNames(storeNumber: string, cancelToken?: CancelToken): Promise<Result<string[]>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<string[]>> = await this.axiosInstance.get('autoshiptemplatenames', { headers, cancelToken });
    return response.data;
  }

  async downloadItemsCsv(storeNumber: string, id: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    const headers = { 'ews-storeNumber': storeNumber ?? '' };
    const response: AxiosResponse<Result<FileResponse>> = await this.axiosInstance.get(`downloaditemscsv/${id}`, { headers, cancelToken });
    return response.data;
  }

  async uploadTemplateItemsCsv(storeNumber: string | undefined, orderTypeId: number, request: FileRequest, cancelToken?: CancelToken): Promise<Result<Item[]>> {
    const headers = { 'ews-storeNumber': storeNumber ?? '' };
    const response: AxiosResponse<Result<Item[]>> = await this.axiosInstance.post(`uploadtemplateitemscsv/${orderTypeId}`, request, { headers, cancelToken });
    return response.data;
  }

  async archiveTemplate(storeNumber: string, templateId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put(`archivetemplate/${templateId}`, null, { headers, cancelToken });
    return response.data;
  }

  async deleteTemplate(storeNumber: string, templateId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(`deletetemplate/${templateId}`, { headers, cancelToken });
    return response.data;
  }

  // Autoship Schedules
  async createOrUpdateAutoshipSchedule(request: AutoshipSchedule, storeNumber: string, cancelToken?: CancelToken): Promise<Result<number>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<number>> = await this.axiosInstance.post('autoshipschedule', request, { headers, cancelToken });
    return response.data;
  }

  async getAutoshipSchedules(options: AutoshipmentSchedulesFilterOptions, storeNumber: string, cancelToken?: CancelToken): Promise<Result<PaginatedList<AutoshipScheduleModel>>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const params = new URLSearchParams({
      Name: options.name ?? '',
      TemplateId: options.templateId?.toString() ?? '',
      Sort: options.sort ?? '',
      Direction: options.direction ?? '',
      PageNumber: options.pageNumber.toString(),
      PageSize: options.pageSize.toString(),
    }).toString();
    const response: AxiosResponse<Result<PaginatedList<AutoshipScheduleModel>>> = await this.axiosInstance.get(`autoshipschedule?${params}`, { headers, cancelToken });
    return response.data;
  }

  async getAutoshipScheduleById(id: number, storeNumber: string, cancelToken?: CancelToken): Promise<Result<AutoshipScheduleModel>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<AutoshipScheduleModel>> = await this.axiosInstance.get(`autoshipschedule/${id}`, { headers, cancelToken });
    return response.data;
  }

  async deleteSchedule(storeNumber: string, scheduleId: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(`deleteschedule/${scheduleId}`, { headers, cancelToken });
    return response.data;
  }

  async getDeliverySchedulesForStores(companyId: number, orderTypeId: number, stores: string, cancelToken?: CancelToken): Promise<Result<StoreAvailableODS[]>> {
    const params = new URLSearchParams({
      CompanyId: companyId.toString(),
      OrderTypeId: orderTypeId.toString(),
      Stores: stores,
    }).toString();
    const response: AxiosResponse<Result<StoreAvailableODS[]>> = await this.axiosInstance.get(`DeliverySchedulesForStores?${params}`, { cancelToken });
    return response.data;
  }

  async getAutoshipReasons(storeNumber: string, cancelToken?: CancelToken): Promise<Result<IdNameModel[]>> {
    const headers = { 'ews-storeNumber': storeNumber };
    const response: AxiosResponse<Result<IdNameModel[]>> = await this.axiosInstance.get('autoshipreasons', { headers, cancelToken });
    return response.data;
  }

  // EDN Orders
  async getOrderEdn(storeNumber?: string, generateNew?: boolean, orderId?: string, cancelToken?: CancelToken): Promise<RetailerEdnOrderModel> {
    const params = new URLSearchParams({
      generateNew: generateNew ? 'true' : 'false',
      orderId: orderId ?? '',
    }).toString();
    const headers = { 'ews-storeNumber': storeNumber ?? '' };
    const response: AxiosResponse<Result<RetailerEdnOrderModel>> = await this.axiosInstance.post(`fetchOrderEdnData?${params}`, null, { cancelToken, headers });
    return response.data.data;
  }

  
  public async getODS(options: DeliveryScheduleFilterOptions, storeNumber: string, cancelToken?: CancelToken): Promise<Result<PaginatedList<DeliveryScheduleModel>>> {
    try {
      const params = new URLSearchParams({
        InstanceId: options.instanceId?.toString(),
        MarketId: options.marketId?.toString(),
        DeliveryDateFrom: options.deliveryDateFrom?.toString(),
        DeliveryDateTo: options.deliveryDateTo?.toString(),
        CutOffDateFrom: options.cutOffDateFrom?.toString(),
        CutOffDateTo: options.cutOffDateTo?.toString(),
        OrderTypes: options.orderTypes?.toString(),
        SupplierNumber: options.supplierNumber?.toString(),
        Stores: options.stores?.toString(),
        PageNumber: options.pageNumber.toString(),
        PageSize: options.pageSize.toString(),
        Sort: options.sort ?? '',
        Direction: options.direction ?? '',
      }).toString();

      const headers = { 'ews-storeNumber': storeNumber };
      const response: AxiosResponse<Result<PaginatedList<DeliveryScheduleModel>>> = await this.axiosInstance.get(`ods?${params}`, { headers, cancelToken });
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async exportODS(options: DeliveryScheduleFilterOptions, storeNumber: string, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const params = new URLSearchParams({
        InstanceId: options.instanceId?.toString(),
        MarketId: options.marketId?.toString(),
        DeliveryDateFrom: options.deliveryDateFrom?.toString(),
        DeliveryDateTo: options.deliveryDateTo?.toString(),
        CutOffDateFrom: options.cutOffDateFrom?.toString(),
        CutOffDateTo: options.cutOffDateTo?.toString(),
        OrderTypes: options.orderTypes?.toString(),
        SupplierNumber: options.supplierNumber?.toString(),
        Stores: options.stores?.toString(),
      }).toString();

      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response = await this.axiosInstance.get<Result<FileResponse>>(`ods/export?${params}`, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async validateODS(request: FileRequest, storeNumber: string, instanceId: number, marketId: number, cancelToken?: CancelToken): Promise<Result<ODSImportResultModel>> {
    try {
      const params = new URLSearchParams({
        InstanceId: instanceId?.toString(),
        MarketId: marketId?.toString(),
      }).toString();

      const headers = { 'ews-storeNumber': storeNumber ?? '' };

      const response: AxiosResponse<Result<ODSImportResultModel>> = await this.axiosInstance.post(`ods/validate?${params}`, request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async importODS(request: FileRequest, storeNumber: string, instanceId: number, marketId: number, languageCode:string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const params = new URLSearchParams({
        InstanceId: instanceId?.toString(),
        MarketId: marketId?.toString(),
        LanguageCode: languageCode,
      }).toString();

      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post(`ods/import?${params}`, request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async updateBulkODSDate(request: BulkODSDateUpdateModel, storeNumber: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.put('ods/bulk', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async deleteBulkODS(ids: number[], storeNumber: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const params = new URLSearchParams({
        Ids: ids.join(','),
      }).toString();

      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.delete(`ods?${params}`, { 
        headers, 
        cancelToken,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

  public async addODS(request: AddODSModel, storeNumber: string, cancelToken?: CancelToken): Promise<Result<boolean>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response: AxiosResponse<Result<boolean>> = await this.axiosInstance.post('ods/add', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }
  
  public async generateForecastOrdersReport(storeNumber: string, request: ForecastOrdersReportRequest, cancelToken?: CancelToken): Promise<Result<FileResponse>> {
    try {
      const headers = { 'ews-storeNumber': storeNumber ?? '' };
      const response = await this.axiosInstance.post<Result<FileResponse>>('Orders/GenerateForecastOrdersReport', request, { headers, cancelToken });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Axios error:', error.message);
      } else {
        console.error('Unexpected error:', error);
      }
      throw error;
    }
  }

}
